/**
 *
 * Implement Details Page
 *
 */
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card,
  Descriptions,
  Dropdown,
  Menu,
  PageHeader,
  Tag,
  Modal,
  notification,
} from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, RouteComponentProps } from 'react-router';

import { RouteConstants } from 'app/routes';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import { useArchiveImplementMutation, useGetImplementByIdQuery } from 'app/api';
import { MainContainer } from 'app/components/LayoutComponents/LayoutComponents';
import { EllipsisIcon } from 'app/components/CommonIcons/CommonIcons';
import { useGlobalSlice } from 'app/containers/Global/slice';
import ImplementDrawer from '../../parts/ImplementDrawer/ImplementDrawer';
import PermissionsWrapper from 'app/components/PermissionsWrapper/PermissionsWrapper';
import { PermissionsEnum } from 'app/containers/User/slice/types';

interface IReactRouterParams {
  implementId: string;
  companyId: string;
}

interface IProps extends RouteComponentProps<IReactRouterParams> {}

const ImplementDetailsPage: FC<IProps> = ({ history, match: { params } }) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const globalActions = useGlobalSlice().actions;

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [
    archiveImplement,
    { isLoading: isArchiveLoading },
  ] = useArchiveImplementMutation();

  const envId = useSelector(selectSelectedEnvId);

  const implementId = Number(params.implementId);
  const companyId = Number(params.companyId);

  const { data: implement, isFetching } = useGetImplementByIdQuery(
    { envId: envId as number, implementId, companyId },
    { skip: !envId || !implementId || !companyId },
  );

  const onArchive = () => {
    Modal.confirm({
      title: t(
        'Implement.ArchiveConfirm',
        'Are you sure you want to archive this implement?',
      ),
      onOk: onArchiveConfirm,
      okText: t('Common.Yes', 'Yes'),
      cancelText: t('Common.No', 'No'),
    });
  };

  const goBackToList = () => {
    history.push(
      generatePath(RouteConstants.garage.implementList, {
        envId: envId as number,
      }),
    );
  };

  const onArchiveConfirm = async () => {
    try {
      await archiveImplement({
        envId: envId as number,
        companyId,
        implementId,
      }).unwrap();

      goBackToList();

      notification.success({
        message: t('Implement.ArchivedSuccessfully', {
          defaultValue: 'Implement "{{implementModel}}" archived successfully',
          implementModel: implement?.model,
        }),
        placement: 'bottomRight',
      });
    } catch (error) {
      dispatch(globalActions.handleError(error));
    }
  };

  return (
    <>
      <PageHeader
        onBack={goBackToList}
        className="page-header"
        title={implement?.model}
      />
      <MainContainer>
        <Card
          style={{ width: '100%', minHeight: 'calc(100vh - 190px)' }}
          title={t('Transport.Information', 'Information')}
          loading={isFetching || isArchiveLoading}
          extra={[
            <PermissionsWrapper
              requiredEditPermissions={[PermissionsEnum.garageAdministration]}
              key="add"
            >
              <Dropdown
                trigger={['click']}
                overlay={
                  <Menu>
                    <Menu.Item onClick={() => setIsDrawerOpen(true)}>
                      {t('Transport.ChangeInformation', 'Change information')}
                    </Menu.Item>

                    <Menu.Item onClick={onArchive}>
                      {t('Common.Archive', 'Archive')}
                    </Menu.Item>
                  </Menu>
                }
              >
                <Button type="primary" shape="circle">
                  <EllipsisIcon size={14} />
                </Button>
              </Dropdown>
            </PermissionsWrapper>,
          ]}
        >
          <Descriptions bordered column={2}>
            <Descriptions.Item label={t('Transport.Model', 'Model')}>
              {implement?.model ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item
              label={t('Transport.Year', 'Year of production')}
            >
              {implement?.year ?? '-'}
            </Descriptions.Item>

            <Descriptions.Item label={t('Transport.Type', 'Type')}>
              {implement?.type.name ?? '-'}
            </Descriptions.Item>

            <Descriptions.Item
              label={t('Implement.ImpementWidth', 'Implement width (m)')}
            >
              {implement?.width ?? '-'}
            </Descriptions.Item>

            <Descriptions.Item label={'Bluetooth ID'}>
              {implement?.gpsImplement?.name ?? '-'}
            </Descriptions.Item>

            <Descriptions.Item label={t('Transport.Ownership', 'Ownership')}>
              {implement?.company.name}
            </Descriptions.Item>

            <Descriptions.Item
              span={2}
              label={t('Implement.MainPerformingTask', 'Main performing task')}
            >
              <Tag>{implement?.defaultTaskCategory?.name ?? '-'}</Tag>
            </Descriptions.Item>

            <Descriptions.Item
              span={2}
              label={t('Implement.PerformingTask', 'Performing task')}
            >
              {implement?.taskCategories.length
                ? implement?.taskCategories?.map(taskCategory => (
                    <Tag key={taskCategory.id}>{taskCategory.name}</Tag>
                  ))
                : '-'}
            </Descriptions.Item>

            {implement?.accountingMethods.map(accountingMethod => (
              <React.Fragment key={accountingMethod.id}>
                <Descriptions.Item
                  span={2}
                  label={t('Transport.CalculationMethod', 'Calculation method')}
                >
                  <Tag>{accountingMethod.name}</Tag>
                </Descriptions.Item>
                <Descriptions.Item
                  span={2}
                  label={t('Transport.CostPrice', 'Cost price')}
                >
                  {accountingMethod.costPrice ?? '-'}
                </Descriptions.Item>
                <Descriptions.Item
                  span={2}
                  label={t('Transport.SellPrice', 'Sell price')}
                >
                  {accountingMethod.sellPrice ?? '-'}
                </Descriptions.Item>
              </React.Fragment>
            ))}
          </Descriptions>
        </Card>
      </MainContainer>
      <ImplementDrawer
        implement={implement}
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
      />
    </>
  );
};

export default ImplementDetailsPage;
